// Handwritten digit templates for recognizer.js.
// Each template is a list of strokes; each stroke is a list of [x, y] points in a 0-100 box (y grows down).
// `curve` smooths key points (Catmull-Rom) for round shapes; `line` keeps sharp corners. Stroke order and
// direction don't matter to the recognizer, and it adds rotated, slanted and stretched copies of every
// template when it loads, so these only need to cover the different *shapes* people use for each digit:
// open and closed 4s and 0s, 7s with crossbars, 1s with flags and bases, 2s with a bottom loop, and so on.

const arc = (cx, cy, rx, ry, fromDeg, toDeg, steps = 24) => {
  const points = [];
  for (let i = 0; i <= steps; i += 1) {
    const angle = ((fromDeg + ((toDeg - fromDeg) * i) / steps) * Math.PI) / 180;
    points.push([cx + rx * Math.cos(angle), cy + ry * Math.sin(angle)]);
  }
  return points;
};

export const curve = (...keys) => {
  if (keys.length < 3) return keys;
  const points = [];
  for (let i = 0; i < keys.length - 1; i += 1) {
    const p0 = keys[Math.max(0, i - 1)];
    const p1 = keys[i];
    const p2 = keys[i + 1];
    const p3 = keys[Math.min(keys.length - 1, i + 2)];
    for (let s = 0; s < 6; s += 1) {
      const t = s / 6;
      const t2 = t * t;
      const t3 = t2 * t;
      points.push([0, 1].map((k) => 0.5 * ((2 * p1[k]) + (-p0[k] + p2[k]) * t
        + (2 * p0[k] - 5 * p1[k] + 4 * p2[k] - p3[k]) * t2 + (-p0[k] + 3 * p1[k] - 3 * p2[k] + p3[k]) * t3)));
    }
  }
  points.push(keys[keys.length - 1]);
  return points;
};

const line = (...keys) => keys;
const join = (...parts) => parts.flat();

export const DIGIT_TEMPLATES = {
  0: [
    [arc(50, 50, 30, 45, -90, 270)],
    [arc(50, 50, 20, 45, -90, 270)],
    [arc(50, 50, 36, 44, -90, 270)],
    [arc(50, 50, 30, 45, -60, 240)], // open top
    [arc(50, 50, 28, 45, -120, 285)], // overlapping start
    [curve([55, 6], [30, 15], [22, 50], [32, 88], [55, 94], [74, 75], [76, 35], [62, 8], [48, 10])],
    [curve([44, 8], [26, 30], [26, 70], [44, 94], [66, 88], [76, 56], [70, 20], [54, 6])],
  ],
  1: [
    [line([50, 5], [50, 95])],
    [line([34, 24], [52, 5], [52, 95])],
    [line([34, 24], [52, 5], [52, 95]), line([32, 95], [72, 95])],
    [line([58, 5], [44, 95])],
    [line([38, 18], [56, 5], [48, 95])],
    [curve([46, 5], [51, 35], [53, 65], [50, 95])],
    [line([40, 14], [52, 5], [50, 95]), line([36, 95], [66, 95])],
  ],
  2: [
    [join(curve([22, 28], [34, 9], [55, 5], [74, 18], [72, 40], [50, 64], [24, 92]), line([24, 92], [80, 92]))],
    [join(curve([24, 22], [45, 6], [70, 12], [72, 34], [58, 55], [22, 94]), line([22, 94], [84, 90]))],
    [join(arc(50, 32, 28, 26, -170, 20, 16), line([76, 41], [20, 94], [82, 94]))],
    [join(curve([22, 30], [40, 6], [68, 8], [74, 30], [56, 60], [30, 86], [24, 94], [40, 88], [60, 90], [82, 92]))],
    // loop at the bottom
    [curve([24, 24], [42, 6], [68, 10], [72, 32], [54, 58], [30, 82], [20, 94], [32, 98], [38, 88], [28, 86], [40, 92], [62, 92], [82, 92])],
    [join(curve([20, 30], [36, 8], [62, 6], [74, 26], [62, 52], [36, 78], [24, 92], [30, 82], [42, 94]), line([42, 94], [80, 92]))],
    // Z-like
    [line([22, 12], [50, 5], [74, 16], [22, 92], [80, 92])],
  ],
  3: [
    [join(arc(48, 27, 27, 22, -160, 90, 16), arc(48, 72, 30, 23, -90, 160, 16))],
    [curve([24, 14], [45, 4], [68, 12], [68, 32], [44, 48], [70, 60], [74, 82], [50, 96], [24, 86])],
    [join(line([24, 6], [74, 6], [44, 42]), curve([44, 42], [68, 50], [76, 72], [62, 92], [40, 95], [22, 84]))],
    [curve([28, 18], [50, 6], [70, 18], [60, 40], [42, 48], [62, 54], [76, 74], [60, 94], [26, 90])],
    [arc(46, 28, 26, 22, -160, 90, 14), arc(46, 72, 30, 23, -90, 160, 14)], // two strokes
    [join(line([26, 6], [72, 8], [48, 40]), arc(46, 68, 28, 27, -90, 150, 16))],
  ],
  4: [
    [line([58, 5], [18, 66], [86, 66]), line([66, 32], [66, 96])],
    [line([62, 5], [16, 66], [86, 66]), line([62, 5], [62, 96])],
    [line([26, 5], [24, 56], [82, 56]), line([68, 8], [68, 96])],
    [line([62, 96], [62, 5], [16, 66], [86, 66])],
    [line([30, 5], [20, 58], [80, 58]), line([66, 20], [64, 96])],
    [line([50, 5], [20, 60], [84, 60]), line([60, 40], [60, 96])],
    [line([24, 5], [22, 52], [72, 54]), line([72, 6], [72, 96])], // open, tall upright
    [line([64, 96], [64, 5], [20, 62], [84, 64])],
  ],
  5: [
    [join(line([30, 6], [27, 45]), arc(48, 68, 28, 25, -140, 140, 18)), line([30, 6], [76, 6])],
    [join(line([76, 6], [30, 6], [27, 45]), arc(48, 68, 28, 25, -140, 140, 18))],
    [join(line([32, 8], [26, 46]), curve([26, 46], [50, 38], [72, 52], [72, 78], [52, 94], [24, 86])), line([32, 8], [74, 6])],
    [curve([74, 6], [30, 6], [27, 44], [50, 38], [72, 52], [72, 78], [52, 94], [24, 86])],
    [join(line([34, 6], [30, 42]), curve([30, 42], [58, 40], [74, 62], [64, 88], [40, 96], [24, 88])), line([40, 6], [78, 8])],
    [join(line([28, 8], [30, 44]), curve([30, 44], [56, 36], [74, 52], [70, 82], [44, 96], [22, 84])), line([30, 8], [70, 4])],
  ],
  6: [
    [curve([70, 8], [45, 15], [30, 40], [28, 70], [40, 92], [60, 92], [72, 75], [65, 57], [45, 55], [30, 68])],
    [curve([64, 5], [40, 25], [28, 55], [30, 82], [50, 95], [70, 82], [68, 60], [48, 54], [30, 66])],
    [join(curve([72, 6], [48, 18], [32, 44], [27, 70]), arc(50, 72, 23, 22, 180, 540, 20))],
    [curve([66, 6], [44, 22], [30, 52], [32, 84], [52, 95], [70, 80], [64, 60], [46, 60])], // open loop
    [join(line([60, 5], [32, 58]), curve([32, 58], [30, 82], [48, 95], [68, 84], [66, 62], [48, 56], [32, 66]))], // straight stem
  ],
  7: [
    [line([20, 8], [80, 8], [45, 95])],
    [line([20, 8], [80, 8], [45, 95]), line([35, 52], [72, 52])],
    [join(line([20, 8], [80, 8]), curve([80, 8], [62, 40], [52, 70], [48, 95]))],
    [line([20, 18], [24, 8], [80, 8], [42, 95])],
    [line([20, 8], [80, 8]), line([78, 8], [42, 95])],
    [line([22, 8], [78, 8], [58, 95])],
    [line([22, 8], [78, 8]), line([78, 8], [48, 95]), line([40, 50], [70, 52])],
  ],
  8: [
    [curve([66, 15], [50, 5], [32, 14], [34, 34], [50, 48], [68, 62], [68, 84], [50, 95], [32, 84], [32, 62], [50, 48], [66, 34], [66, 15])],
    [join(arc(50, 27, 20, 21, 0, 360, 18)), join(arc(50, 71, 25, 24, -90, 270, 18))],
    [curve([64, 10], [44, 4], [32, 20], [44, 40], [66, 60], [66, 86], [46, 96], [30, 82], [36, 60], [60, 38], [68, 20], [58, 6])],
    [arc(50, 28, 18, 22, -90, 270, 16), arc(50, 72, 22, 22, -90, 270, 16)],
    [curve([38, 12], [56, 6], [66, 22], [44, 44], [30, 68], [44, 94], [68, 84], [64, 58], [40, 38], [34, 18], [44, 8])],
  ],
  9: [
    [curve([70, 25], [55, 8], [35, 12], [30, 32], [45, 45], [65, 38], [70, 25], [68, 60], [65, 95])],
    [join(arc(50, 28, 22, 22, 0, 360, 20)), line([72, 28], [72, 96])],
    [join(arc(48, 28, 22, 22, -10, 350, 20), curve([70, 24], [68, 60], [56, 88], [36, 95]))],
    [curve([68, 20], [50, 5], [30, 18], [36, 42], [60, 42], [70, 22], [70, 60], [66, 95])],
    [arc(48, 28, 22, 22, 0, 360, 18), line([70, 28], [64, 96])],
    [curve([66, 30], [50, 10], [32, 18], [34, 40], [56, 44], [68, 28], [68, 70], [60, 92], [40, 94])],
  ],
};
